import React, { useState, useEffect } from 'react';
import { Box, Grid, Paper, Button, FormHelperText } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import PrimaryButton from '../../../components/Button/Button';
import { uploadDocuments } from '../../../sevices/documentApi';
import { useForm, FormProvider, Controller } from 'react-hook-form';

// Import sub-components
import ClientSelector from './selectors/ClientSelector';
import BuildingSelector from './selectors/BuildingSelector';
import UnitSelector from './selectors/UnitSelector';
import FileUploader from './FileUploader';
import SelectedFilesList from './SelectedFilesList';
import useMutate from '../../../hooks/useMutate';

const DocumentUploadForm = ({ onUploadComplete }) => {
  const [selectedBuildingData, setSelectedBuildingData] = useState(null);
  const [files, setFiles] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  const { mutate: uploadDocumentsFn, isPending: isUploading } = useMutate(
    uploadDocuments,
    [],
    () => {
      setFiles([]);
      reset({
        clientId: "",
        buildingId: "",
        unitId: "",
        documentTitles: {},
      });
      setSelectedBuildingData(null);
      // Notify parent component of successful upload
      if (onUploadComplete) {
        onUploadComplete();
      }
    }
  );
  
  // Set up React Hook Form
  const methods = useForm({
    mode: 'onSubmit',
    defaultValues: {
      clientId: '',
      buildingId: '',
      unitId: '',
      documentTitles: {}
    }
  });
  
  const { 
    control, 
    handleSubmit, 
    watch, 
    setValue, 
    reset 
  } = methods;
  
  // Watch for changes to use in effects and conditions
  const selectedClient = watch('clientId');
  const selectedBuilding = watch('buildingId');

  // Reset unit selection when building changes
  useEffect(() => {
    if (!selectedBuilding) {
      setValue('unitId', '');
    }
  }, [selectedBuilding, setValue]);

  const handleFileAdd = (newFiles) => {
    const updatedFiles = [...files, ...newFiles];
    setFiles(updatedFiles);
    
    // Initialize document titles in form state
    newFiles.forEach(file => {
      setValue(`documentTitles.${file.id}`, file.title);
    });
  };

  const handleTitleChange = (id, value) => {
    // Update files array for backward compatibility
    setFiles(prev => prev.map(file => 
      file.id === id ? { ...file, title: value } : file
    ));
    
    // Update form state for React Hook Form
    setValue(`documentTitles.${id}`, value);
  };

  const handleRemoveFile = (id) => {
    setFiles(prev => prev.filter(file => file.id !== id));
    
    // Remove from form state
    const currentTitles = { ...methods.getValues('documentTitles') };
    delete currentTitles[id];
    setValue('documentTitles', currentTitles);
  };

  const onSubmit = async (data) => {
    // Check if files are selected
    if (files.length === 0) {
      alert('Please select at least one file');
      return;
    }

    // Check if all files have titles and validate them
    let hasErrors = false;
    
    files.forEach(file => {
      if (!file.title || file.title.trim() === '') {
        methods.setError(`documentTitles.${file.id}`, {
          type: 'required',
          message: 'Document title is required'
        });
        hasErrors = true;
      }
    });
    
    if (hasErrors) {
      alert('Please provide a title for all documents');
      return;
    }

    setIsLoading(true);

    try {
      const formData = new FormData();
      // formData.append('client_id', data.clientId);
      // formData.append('building_id', data.buildingId);
      formData.append('unit_id', data.unitId);

      files.forEach((fileObj) => {
        formData.append('files', fileObj.file);
      });
      files.forEach((fileObj) => {
        formData.append("titles", fileObj.title);
      });
      
       uploadDocumentsFn(formData);

      
    } catch (error) {
      console.error('Error uploading documents:', error);
      alert('Error uploading documents');
    } finally {
      setIsLoading(false);
    }
  };
  
  // We're keeping this for reference but not using it directly anymore
  // const handleUpload = () => {
  //   handleSubmit(onSubmit)();
  // };

  return (
    <Box sx={{ mt: 3 }}>
      <Paper elevation={0} sx={{ padding: 3, borderRadius: 2 }}>
        <p className="text-sm-regular">
          Upload documents related to a specific building unit. Once uploaded,
          documents will be accessible and editable from the unit detail page
          under the corresponding building.
        </p>

        <FormProvider {...methods}>
          <form onSubmit={handleSubmit(onSubmit)}>
            <Grid container spacing={3}>
              {/* Client Selector */}
              <Controller
                name="clientId"
                control={control}
                rules={{ required: "Client is required" }}
                render={({ field, fieldState: { error } }) => (
                  <>
                    <ClientSelector
                      selectedClient={field.value}
                      setSelectedClient={(value) => field.onChange(value)}
                    />
                    {error && (
                      <FormHelperText sx={{ ml: 4 }} error>
                        {error.message}
                      </FormHelperText>
                    )}
                  </>
                )}
              />

              {/* Building Selector */}
              <Controller
                name="buildingId"
                control={control}
                rules={{ required: "Building is required" }}
                render={({ field, fieldState: { error } }) => (
                  <>
                    <BuildingSelector
                      selectedClient={selectedClient}
                      selectedBuilding={field.value}
                      setSelectedBuilding={(value) => field.onChange(value)}
                      setSelectedBuildingData={setSelectedBuildingData}
                    />
                    {error && (
                      <FormHelperText sx={{ ml: 4 }} error>
                        {error.message}
                      </FormHelperText>
                    )}
                  </>
                )}
              />

              {/* Unit Selector */}
              <Controller
                name="unitId"
                control={control}
                rules={{ required: "Unit is required" }}
                render={({ field, fieldState: { error } }) => (
                  <>
                    <UnitSelector
                      selectedBuilding={selectedBuilding}
                      selectedUnit={field.value}
                      setSelectedUnit={(value) => field.onChange(value)}
                      buildingData={selectedBuildingData}
                    />
                    {error && (
                      <FormHelperText sx={{ ml: 4 }} error>
                        {error.message}
                      </FormHelperText>
                    )}
                  </>
                )}
              />

              {/* File Uploader */}
              {files.length === 0 && (
                <FileUploader onFileChange={handleFileAdd} />
              )}

              {/* Selected Files List */}
              <SelectedFilesList
                files={files}
                onTitleChange={handleTitleChange}
                onRemoveFile={handleRemoveFile}
                formMethods={methods}
              />

              {/* Add More Files button */}
              {files.length > 0 && (
                <Grid item xs={12}>
                  <Button
                    startIcon={<AddIcon />}
                    onClick={() =>
                      document.getElementById("add-more-files-input").click()
                    }
                    sx={{
                      color: "#16304F",
                      textTransform: "none",
                      fontWeight: 500,
                      padding: "4px 8px",
                      "&:hover": {
                        backgroundColor: "none",
                      },
                      textDecoration: "underline",
                    }}
                  >
                    Add More Files
                  </Button>
                  <input
                    id="add-more-files-input"
                    type="file"
                    multiple
                    onChange={(e) => {
                      const selectedFiles = Array.from(e.target.files);
                      const newFiles = selectedFiles.map((file) => ({
                        file,
                        title:
                          file.name.split(".").slice(0, -1).join(".") ||
                          file.name,
                        id: Math.random().toString(36).substring(2, 9),
                      }));
                      handleFileAdd(newFiles);
                      e.target.value = "";
                    }}
                    style={{ display: "none" }}
                  />
                </Grid>
              )}

              {/* Upload Button */}
              <Grid item xs={12} sx={{ textAlign: "right", mt: 2 }}>
                <PrimaryButton
                  variant="contained"
                  color="primary"
                  type="submit"
                  disabled={isLoading || isUploading || files.length === 0}
                  className={"capitalize primary-btn-class"}
                >
                  {isUploading  ? "Uploading..." : "Upload Documents"}
                </PrimaryButton>
              </Grid>
            </Grid>
          </form>
        </FormProvider>
      </Paper>
    </Box>
  );
};

export default DocumentUploadForm;